import { useEffect, useState, useMemo } from "react";
import { Box, Button, Paper, Typography, Chip, TextField, FormControl, InputLabel, Select, MenuItem, SelectChangeEvent, Tabs, Tab } from "@mui/material";
import { ApiService } from "../../../services";
import { UserInfo } from "../../../common/models";
import { useConfig } from "../../../config/ConfigProvider";

interface ApprovalManagementProps {
    apiService: ApiService;
}

const ApprovalManagement: React.FC<ApprovalManagementProps> = ({ apiService }) => {
    const config = useConfig();
    const [unassignedUsers, setUnassignedUsers] = useState<UserInfo[]>([]);
    const [approvedUsers, setApprovedUsers] = useState<UserInfo[]>([]);
    const [allUsers, setAllUsers] = useState<UserInfo[]>([]);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [approvedSearchTerm, setApprovedSearchTerm] = useState<string>("");
    const [rolesSearchTerm, setRolesSearchTerm] = useState<string>("");
    const [filterType, setFilterType] = useState<string>("both");
    const [activeSubTab, setActiveSubTab] = useState<number>(0);
    const [loading, setLoading] = useState<boolean>(false);

    const fetchUnassignedUsers = async () => {
        try {
            setLoading(true);
            // Fetch users without 'approved' role
            const users = await apiService.get<UserInfo[]>(
                "/api/admin/users/without-role/approved",
                new AbortController().signal
            );
            setUnassignedUsers(users);
        } catch (error) {
            console.error("Error fetching unassigned users:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchApprovedUsers = async () => {
        try {
            setLoading(true);
            // Fetch users with 'approved' role
            const users = await apiService.get<UserInfo[]>(
                "/api/admin/users/by-role/approved",
                new AbortController().signal
            );
            setApprovedUsers(users);
        } catch (error) {
            console.error("Error fetching approved users:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchAllUsers = async () => {
        try {
            // Fetch all users for search functionality
            const users = await apiService.get<UserInfo[]>(
                "/api/admin/users",
                new AbortController().signal
            );
            setAllUsers(users);
        } catch (error) {
            console.error("Error fetching all users:", error);
        }
    };

    const approveUser = async (email: string) => {
        try {
            // Add 'approved' role to user (using email as identifier)
            await apiService.postJson<{}, void>(
                `/api/admin/users/${encodeURIComponent(email)}/roles/approved`,
                {},
                new AbortController().signal
            );
            fetchUnassignedUsers();
            fetchApprovedUsers();
        } catch (error) {
            console.error("Error approving user:", error);
        }
    };

    const removeUser = async (email: string) => {
        try {
            // Remove 'approved' role from user using fetch directly since delete method requires body
            await fetch(`${config.apiBaseUrl}/api/admin/users/${encodeURIComponent(email)}/roles/approved`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            fetchApprovedUsers();
            fetchUnassignedUsers();
        } catch (error) {
            console.error("Error removing user:", error);
        }
    };

    const addRole = async (email: string, role: string) => {
        try {
            await apiService.postJson<{}, void>(
                `/api/admin/users/${encodeURIComponent(email)}/roles/${role}`,
                {},
                new AbortController().signal
            );
            fetchApprovedUsers();
        } catch (error) {
            console.error(`Error adding ${role} role:`, error);
        }
    };

    const removeRole = async (email: string, role: string) => {
        try {
            await fetch(`${config.apiBaseUrl}/api/admin/users/${encodeURIComponent(email)}/roles/${role}`, {
                method: 'DELETE',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            fetchApprovedUsers();
        } catch (error) {
            console.error(`Error removing ${role} role:`, error);
        }
    };

    // Filter unassigned users based on search term with pagination limit
    const filteredUnassignedUsers = useMemo(() => {
        let filtered = unassignedUsers;
        
        // Apply search filter
        if (searchTerm) {
            // Search in all users if search term exists
            const searchLower = searchTerm.toLowerCase();
            filtered = allUsers.filter(user => {
                const hasApprovedRole = user.roles?.includes('approved');
                const matchesSearch = 
                    user.displayName?.toLowerCase().includes(searchLower) ||
                    user.email?.toLowerCase().includes(searchLower);
                return !hasApprovedRole && matchesSearch;
            });
        }
        
        // Limit to 100 results
        return filtered.slice(0, 100);
    }, [unassignedUsers, allUsers, searchTerm]);

    // Filter approved users based on subscription status with pagination limit
    const filteredApprovedUsers = useMemo(() => {
        let filtered = approvedUsers;
        
        // Apply search filter
        if (approvedSearchTerm) {
            const searchLower = approvedSearchTerm.toLowerCase();
            filtered = filtered.filter(user => 
                user.displayName?.toLowerCase().includes(searchLower) ||
                user.email?.toLowerCase().includes(searchLower)
            );
        }
        
        // Apply subscription/exemption filter
        if (filterType === "subscribed") {
            filtered = filtered.filter(user => user.isSubscribed === true);
        } else if (filterType === "exempt") {
            filtered = filtered.filter(user => !user.isSubscribed);
        }
        
        // Limit to 100 results
        return filtered.slice(0, 100);
    }, [approvedUsers, approvedSearchTerm, filterType]);

    // Filter approved users for role management tab
    const filteredRoleUsers = useMemo(() => {
        let filtered = approvedUsers;
        
        if (rolesSearchTerm) {
            const searchLower = rolesSearchTerm.toLowerCase();
            filtered = filtered.filter(user => 
                user.displayName?.toLowerCase().includes(searchLower) ||
                user.email?.toLowerCase().includes(searchLower)
            );
        }
        
        return filtered.slice(0, 100);
    }, [approvedUsers, rolesSearchTerm]);

    const handleFilterChange = (event: SelectChangeEvent<string>) => {
        setFilterType(event.target.value);
    };

    useEffect(() => {
        fetchUnassignedUsers();
        fetchApprovedUsers();
        fetchAllUsers();
    }, []);

    const handleSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setActiveSubTab(newValue);
    };

    return (
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
            {/* Sub-Tabs */}
            <Tabs
                value={activeSubTab}
                onChange={handleSubTabChange}
                textColor="primary"
                indicatorColor="primary"
                sx={{
                    borderBottom: 1,
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    flexShrink: 0,
                    "& .MuiTab-root": {
                        color: "text.secondary",
                        fontWeight: 500,
                        minHeight: 48,
                        transition: "all 0.2s",
                    },
                    "& .Mui-selected": {
                        color: "#e50c20",
                    },
                    "& .MuiTabs-indicator": {
                        bgcolor: "#e50c20",
                        height: 3,
                    },
                }}
            >
                <Tab key="pending" label="Pending Approval" />
                <Tab key="approved" label="Approved Users" />
                <Tab key="roles" label="Role Management" />
            </Tabs>

            {/* Pending Approval Tab */}
            {activeSubTab === 0 && (
                <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <Typography variant="h6" sx={{ flexShrink: 0, mb: 1 }}>
                        Pending Approval
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, mb: 2 }}>
                        Showing up to 100 results. Use search to find specific users.
                    </Typography>
                    <TextField
                        label="Search by name or email"
                        variant="outlined"
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        sx={{ mb: 2, flexShrink: 0 }}
                    />
                    <Box sx={{ overflowY: "auto", flex: 1 }}>
                        {filteredUnassignedUsers.map((user) => (
                            <Paper
                                key={user.userId || user.id || user.email}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: 2,
                                mb: 2,
                                flexDirection: "row",
                                gap: 2,
                                boxShadow: 2,
                            }}
                        >
                            <Typography sx={{ flex: 1, fontSize: "1rem", wordBreak: "break-word" }}>
                                {user.displayName}{user.email ? ` - ${user.email}` : ""}
                            </Typography>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={() => approveUser(user.email)}
                                sx={{
                                    minWidth: 90,
                                    maxWidth: 110,
                                    fontWeight: 700,
                                    fontSize: "0.95rem",
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1,
                                    boxShadow: 1,
                                }}
                            >
                                Approve
                            </Button>
                            </Paper>
                        ))}
                </Box>
            </Box>
            )}

            {/* Approved Users Tab */}
            {activeSubTab === 1 && (
                <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0, mb: 1 }}>
                        <Typography variant="h6">Approved Users</Typography>
                        <FormControl size="small" sx={{ minWidth: 150 }}>
                            <InputLabel>Filter</InputLabel>
                            <Select
                                value={filterType}
                                label="Filter"
                                onChange={handleFilterChange}
                            >
                                <MenuItem value="both">Both</MenuItem>
                                <MenuItem value="exempt">Not Subscribed</MenuItem>
                                <MenuItem value="subscribed">Subscribed</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, mb: 2 }}>
                        Showing up to 100 results. Use search to find specific users.
                    </Typography>
                    <TextField
                        label="Search by name or email"
                        variant="outlined"
                        size="small"
                        value={approvedSearchTerm}
                        onChange={(e) => setApprovedSearchTerm(e.target.value)}
                        sx={{ mb: 2, flexShrink: 0 }}
                    />
                    <Box sx={{ overflowY: "auto", flex: 1 }}>
                        {filteredApprovedUsers.map((user) => (
                            <Paper
                                key={user.userId || user.id || user.email}
                            sx={{
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                p: 2,
                                mb: 2,
                                flexDirection: "row",
                                gap: 2,
                                boxShadow: 2,
                            }}
                        >
                            <Typography sx={{ flex: 1, fontSize: "1rem", wordBreak: "break-word" }}>
                                {user.displayName}{user.email ? ` - ${user.email}` : ""}
                            </Typography>
                            <Chip
                                label={user.isSubscribed ? "Subscribed" : "Not Subscribed"}
                                color={user.isSubscribed ? "success" : "default"}
                                sx={{ mr: 2, fontWeight: 700 }}
                            />
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => removeUser(user.email)}
                                sx={{
                                    minWidth: 90,
                                    maxWidth: 110,
                                    fontWeight: 700,
                                    fontSize: "0.95rem",
                                    borderRadius: 2,
                                    px: 2,
                                    py: 1,
                                    boxShadow: 1,
                                }}
                            >
                                Remove
                            </Button>
                            </Paper>
                        ))}
                </Box>
            </Box>
            )}

            {/* Role Management Tab */}
            {activeSubTab === 2 && (
                <Box sx={{ p: 3, height: "100%", display: "flex", flexDirection: "column", overflow: "hidden" }}>
                    <Typography variant="h6" sx={{ flexShrink: 0, mb: 1 }}>
                        Role Management
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0, mb: 2 }}>
                        Manage admin and developer roles for approved users. Showing up to 100 results.
                    </Typography>
                    <TextField
                        label="Search by name or email"
                        variant="outlined"
                        size="small"
                        value={rolesSearchTerm}
                        onChange={(e) => setRolesSearchTerm(e.target.value)}
                        sx={{ mb: 2, flexShrink: 0 }}
                    />
                    <Box sx={{ overflowY: "auto", flex: 1 }}>
                        {filteredRoleUsers.map((user) => {
                            const hasAdmin = user.roles?.includes('admin');
                            const hasDeveloper = user.roles?.includes('developer');
                            const hasPlayground = user.roles?.includes('playground');
                            const hasAdvanced = user.roles?.includes('advanced');
                            
                            return (
                                <Paper
                                    key={user.userId || user.id || user.email}
                                    sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        p: 2,
                                        mb: 2,
                                        flexDirection: "row",
                                        gap: 2,
                                        boxShadow: 2,
                                    }}
                                >
                                    <Box sx={{ flex: 1 }}>
                                        <Typography sx={{ fontSize: "1rem", wordBreak: "break-word", mb: 0.5 }}>
                                            {user.displayName}{user.email ? ` - ${user.email}` : ""}
                                        </Typography>
                                        <Box sx={{ display: "flex", gap: 1, mt: 1 }}>
                                            {hasAdmin && (
                                                <Chip label="Admin" color="error" size="small" sx={{ fontWeight: 600 }} />
                                            )}
                                            {hasDeveloper && (
                                                <Chip label="Developer" color="info" size="small" sx={{ fontWeight: 600 }} />
                                            )}
                                            {hasPlayground && (
                                                <Chip label="Playground" color="success" size="small" sx={{ fontWeight: 600 }} />
                                            )}
                                            {hasAdvanced && (
                                                <Chip label="Advanced" color="warning" size="small" sx={{ fontWeight: 600 }} />
                                            )}
                                            {!hasAdmin && !hasDeveloper && !hasPlayground && !hasAdvanced && (
                                                <Chip label="No Special Roles" size="small" variant="outlined" />
                                            )}
                                        </Box>
                                    </Box>
                                    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", justifyContent: "flex-end" }}>
                                        {hasAdmin ? (
                                            <Button
                                                variant="outlined"
                                                color="error"
                                                size="small"
                                                onClick={() => removeRole(user.email, 'admin')}
                                                sx={{ minWidth: 100, fontWeight: 600 }}
                                            >
                                                Remove Admin
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                color="error"
                                                size="small"
                                                onClick={() => addRole(user.email, 'admin')}
                                                sx={{ minWidth: 100, fontWeight: 600 }}
                                            >
                                                Add Admin
                                            </Button>
                                        )}
                                        {hasDeveloper ? (
                                            <Button
                                                variant="outlined"
                                                color="info"
                                                size="small"
                                                onClick={() => removeRole(user.email, 'developer')}
                                                sx={{ minWidth: 120, fontWeight: 600 }}
                                            >
                                                Remove Developer
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                color="info"
                                                size="small"
                                                onClick={() => addRole(user.email, 'developer')}
                                                sx={{ minWidth: 120, fontWeight: 600 }}
                                            >
                                                Add Developer
                                            </Button>
                                        )}
                                        {hasPlayground ? (
                                            <Button
                                                variant="outlined"
                                                color="success"
                                                size="small"
                                                onClick={() => removeRole(user.email, 'playground')}
                                                sx={{ minWidth: 120, fontWeight: 600 }}
                                            >
                                                Remove Playground
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                color="success"
                                                size="small"
                                                onClick={() => addRole(user.email, 'playground')}
                                                sx={{ minWidth: 120, fontWeight: 600 }}
                                            >
                                                Add Playground
                                            </Button>
                                        )}
                                        {hasAdvanced ? (
                                            <Button
                                                variant="outlined"
                                                color="warning"
                                                size="small"
                                                onClick={() => removeRole(user.email, 'advanced')}
                                                sx={{ minWidth: 120, fontWeight: 600 }}
                                            >
                                                Remove Advanced
                                            </Button>
                                        ) : (
                                            <Button
                                                variant="contained"
                                                color="warning"
                                                size="small"
                                                onClick={() => addRole(user.email, 'advanced')}
                                                sx={{ minWidth: 120, fontWeight: 600 }}
                                            >
                                                Add Advanced
                                            </Button>
                                        )}
                                    </Box>
                                </Paper>
                            );
                        })}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default ApprovalManagement;